import { Dialog, Modal } from "@material-ui/core";
import { useIdle } from "application/contexts/idle/IdleContext";
import { useKiosk } from "application/contexts/kiosk/KioskContext";
import { useUi } from "application/contexts/ui/UIContext";
import { ErrorCodeType, IError } from "application/models/IError";
import KeyboardPimpad from "areas/payment/keyboardPimpad/KeyboardPimpad";
import { SelectCardType } from "areas/payment/selectCardType/SelectCardType";
import BottomModal from "components/bottomModal/BottomModal";
import {  SystemType } from "domains/kiosk/aggregates/Kiosk";
import { CashlessRepository } from "Infrastructure/repositories/api/CashlessRepository";
import { useAppInsights } from "Infrastructure/repositories/appInsights/AppInsights";
import { getOrderRepository } from "Infrastructure/repositories/localStorage/getOrderRepository";
import { DoCashlessCosumeUseCase } from "modules/order/application/useCases/DoCashlessCosumeUseCase";
import { ICustomer } from "modules/order/domain/models/ICustomer";
import { IEmitedNfce } from "modules/order/domain/models/INfce";
import { IOrder } from "modules/order/domain/models/IOrder";
import { IOrderLog } from "modules/order/domain/models/IOrderLog";
import { IPedidoPosDTO } from "modules/order/domain/models/IPedidoPosDTO";
import React, {
  forwardRef,
  Fragment,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  ICreateDefferedPromise,
  createDefferedPromise,
} from "utils/DefferedPromise";
import { GetError } from "utils/GetError";
import { ReactComponent as Pinpad } from "../../../assets/icons/pinpad.svg";
import { ICardCashless } from "../domain/models/ICashless";
import { Payment } from "../domain/models/IPayment";
import { TransactionType } from "../domain/models/TransactionType";
import UseCashless, { ICashlessHandler } from "./useCashless/UseCashless";
import { UsePinpad } from "./usePinpad/UsePinpad";
import Pix, { IPixHandler } from "./usePix/Pix";
import UseNominalCashless, {
  INominalCashlessHandler,
} from "./useNominalCashless/UseNominalCashless";
import UseWallet, { IWalletHandler } from "./useWallet/UseWallet";

const locaStorageOrderRepository = getOrderRepository();
const cashlessRepository = CashlessRepository();

export interface IPaymentResult {
  order: IOrder;
  payment: Payment;
  paymentType: TransactionType;
  pedidoPos?: IPedidoPosDTO;
}

interface IPaymentProviderProps {
  rechargePayment?: "Recharge" | "Payment" | "WalletRecharge";
}

export interface IPaymentRef {
  open(
    order: IOrder,
    paymentType?: TransactionType,
    nominalCardData?: ICardCashless
  ): Promise<IPaymentResult>;
}

const PaymentProvider: React.ForwardRefRenderFunction<
  IPaymentRef,
  IPaymentProviderProps
> = ({ rechargePayment }, ref) => {
  const pixRef = useRef<IPixHandler>(null);
  const cashlessRef = useRef<ICashlessHandler>(null);
  const walletRef = useRef<IWalletHandler>(null);
  const nominalCashlessRef = useRef<INominalCashlessHandler>(null);
  const paymentRef = useRef<ICreateDefferedPromise<IPaymentResult>>(
    createDefferedPromise()
  );

  const { showLoading } = useUi();
  const { handleDisableIdleTimer, handleActiveIdleTimer } = useIdle();
  const { kiosk } = useKiosk();
  const { addLog } = useAppInsights();
  const installment = useRef(1);

  const orderToPay = useRef<IOrder | null>(null);
  const [inProgress, setInProgress] = useState(false);
  const [showTypeSelection, setShowTypeSelection] = useState(false);
  const [walletRefState, setWalletRef] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const [message, setMessage] = useState<string>("");
  const [keyboardValues, setKeyboardValues] = useState<{
    min: number;
    max: number;
    message: string;
  } | null>(null);

  const clearPinpadValues = useCallback(() => {
    setMessage("");
    setKeyboardValues(null);
  }, []);

  const { doPinpadPayment } = UsePinpad();

  const addOrderLog = useCallback(
    async (
      order: IOrderLog | null,
      message: string,
      pedidoPOS?: IPedidoPosDTO,
      payment?: Payment,
      nfce?: IEmitedNfce[]
    ) => {
      try {
        if (order) {
          let orderLog = { ...order, payment, pedidoPOS, nfce };
          addLog("payment", { message, orderLog });
          await locaStorageOrderRepository.add(orderLog, message);
        }
      } catch (error) {
        GetError(
          error,
          "addOrderLog",
          "PaymentProvider",
          "Falha ao adicionar log",
          ErrorCodeType.GENERAL
        );
      }
    },
    [addLog]
  );

  const callPinpad = useCallback(
    async (type: TransactionType) => {
      if (!orderToPay.current) {
        setInProgress(false);
        setIsProcessing(false);
        handleActiveIdleTimer();
        paymentRef.current.rejecter({
          code: ErrorCodeType.PAYMENT,
          message: "Pedido não encontrado",
          caller: "callPinpad",
          file: "PaymentProvider",
        } as IError);
        return;
      }
      if (!kiosk) {
        throw new Error("Kiosk não informado");
      }
      try {
        // console.log("response: transire ", kiosk?.transireToken);
        const response = await doPinpadPayment(
          orderToPay.current.totalValue,
          type,
          kiosk,
          orderToPay.current.friendlyId ?? orderToPay.current.id
        );
        await addOrderLog(
          orderToPay.current,
          "Retorno pinpad",
          undefined,
          response
        );
        if (!response.success) {
          throw new Error(
            response?.errorMessage ?? "[-001] Falha no pagamento"
          );
        }
        setInProgress(false);
        setIsProcessing(false);
        clearPinpadValues();
        const order = orderToPay.current;
        orderToPay.current = null;
        paymentRef.current.resolver({
          order: {
            ...order,
            payment: response,
            createdAt:
              new Date(response.FinancialTransactionModel.DataCriacao) <
              new Date(order.createdAt)
                ? order.createdAt
                : response.FinancialTransactionModel.DataCriacao,
          },
          payment: response,
          paymentType: type,
        });
      } catch (error) {
        console.log({ error, order: orderToPay.current });
        await addOrderLog(
          orderToPay.current,
          "[-002] Falha no pagamento",
          undefined,
          {
            errorMessage: JSON.stringify(error),
          } as Payment
        );
        setInProgress(false);
        setIsProcessing(false);
        clearPinpadValues();
        orderToPay.current = null;
        handleActiveIdleTimer();
        paymentRef.current.rejecter(
          GetError(
            error,
            "callPinpad",
            "PaymentProvider",
            undefined,
            ErrorCodeType.PAYMENT
          )
        );
      }
    },
    [addOrderLog, clearPinpadValues, doPinpadPayment, handleActiveIdleTimer, kiosk]
  );

  const callPix = useCallback(async () => {
    if (!orderToPay.current) {
      setIsProcessing(false);
      handleActiveIdleTimer();
      paymentRef.current.rejecter({
        code: ErrorCodeType.PAYMENT,
        message: "Pedido não encontrado",
        caller: "callPix",
        file: "PaymentProvider",
      } as IError);
      return;
    }
    try {
      const pixTransaction = await pixRef.current?.open(orderToPay.current);
      if (pixTransaction) {
        const payment = {
          amount: pixTransaction.value,
          success: true,
          paymentMethod: TransactionType.pix,
          FinancialTransactionModel: {
            TipoAdquirente: 10, // meep
          },
        } as Payment;
        const order = orderToPay.current;
        orderToPay.current = null;
        setIsProcessing(false);
        paymentRef.current.resolver({
          order: { ...order, payment, paymentPix: pixTransaction },
          payment,
          paymentType: TransactionType.pix,
        });
      }
    } catch (error) {
      setIsProcessing(false);
      handleActiveIdleTimer();
      GetError(
        error,
        "callPix",
        "PaymentProvider",
        undefined,
        ErrorCodeType.PAYMENT
      );
      paymentRef.current.rejecter(error);
    }
  }, [handleActiveIdleTimer]);

  //Consumo cashless nominal
  const callNominalCashless = useCallback(
    async (nominalCardData: ICardCashless) => {
      if (!orderToPay.current) {
        setIsProcessing(false);
        handleActiveIdleTimer();
        paymentRef.current.rejecter({
          code: ErrorCodeType.PAYMENT,
          message: "Pedido não encontrado!",
          caller: "callNominalCashless",
          file: "PaymentProvider",
        } as IError);
        return;
      }

      const totalValue = orderToPay.current.totalValue;
      const balance = nominalCardData.balance;

      if (
        nominalCardData?.dailyLimit &&
        totalValue > nominalCardData.dailyLimit - nominalCardData.dailyExpense
      ) {
        setIsProcessing(false);
        paymentRef.current.rejecter({
          code: ErrorCodeType.PAYMENT,
          message: "Limite diário excedido",
          caller: "callNominalCashless",
          file: "PaymentProvider",
        } as IError);
        return;
      }

      if (totalValue <= balance) {
      } else if (nominalCardData.allowsNegativeBalance) {
        const negativeBalanceLimit = nominalCardData.negativeBalanceLimit;
        const maxNegativeBalance =
          negativeBalanceLimit ?? Number.NEGATIVE_INFINITY;

        if (negativeBalanceLimit === null || negativeBalanceLimit === 0) {
        } else if (totalValue > balance + maxNegativeBalance) {
          setIsProcessing(false);
          paymentRef.current.rejecter({
            code: ErrorCodeType.PAYMENT,
            message: "Saldo insuficiente",
            caller: "callNominalCashless",
            file: "PaymentProvider",
          } as IError);
          return;
        }
      } else {
        setIsProcessing(false);
        paymentRef.current.rejecter({
          code: ErrorCodeType.PAYMENT,
          message: "Saldo insuficiente",
          caller: "callNominalCashless",
          file: "PaymentProvider",
        } as IError);
        return;
      }

      try {
        const result = await nominalCashlessRef.current?.open(
          orderToPay.current,
          nominalCardData
        );
        if (result && result.success && result.order) {
          setIsProcessing(false);
          paymentRef.current.resolver({
            order: result.order,
            pedidoPos: result.pedidoPOS,
            paymentType: TransactionType.cashless,
            payment: {
              paymentMethod: TransactionType.cashless,
            } as Payment,
          });
        } else {
          setIsProcessing(false);
          paymentRef.current.rejecter(
            GetError(
              new Error("Falha ao pagar com cashless"),
              "callNominalCashless",
              "PaymentProvider",
              undefined,
              ErrorCodeType.PAYMENT
            )
          );
        }
      } catch (error) {
        setIsProcessing(false);
        handleActiveIdleTimer();
        GetError(
          error,
          "callNominalCashless",
          "PaymentProvider",
          undefined,
          ErrorCodeType.PAYMENT
        );
        paymentRef.current.rejecter(error);
      }
    },
    [handleActiveIdleTimer]
  );

  // Pagamento de comandas
  // Consumo pré cashless
  // Consumo pós pago
  const callCashless = useCallback(async () => {
    if (!orderToPay.current) {
      setIsProcessing(false);
      handleActiveIdleTimer();
      paymentRef.current.rejecter({
        code: ErrorCodeType.PAYMENT,
        message: "Pedido não encontrado!",
        caller: "callCashless",
        file: "PaymentProvider",
      } as IError);
      return;
    }

    if (!cashlessRef.current) {
      setIsProcessing(false);
      handleActiveIdleTimer();
      paymentRef.current.rejecter({
        code: ErrorCodeType.PAYMENT,
        message: "Falha ao iniciar provedor cashless",
        caller: "callCashless",
        file: "PaymentProvider",
      } as IError);
      return;
    }

    if (!kiosk) {
      setIsProcessing(false);
      handleActiveIdleTimer();
      paymentRef.current.rejecter({
        code: ErrorCodeType.PAYMENT,
        message: "Falha ao obter configurações do totem",
        caller: "callCashless",
        file: "PaymentProvider",
      } as IError);
      return;
    }

    try {
      const result = await cashlessRef.current.open(orderToPay.current);
      showLoading();
      orderToPay.current = result.order;
      const customer: ICustomer = {
        ...orderToPay.current.customer,
        document:
          orderToPay.current.customer?.document ??
          result.cardData.holderDocument,
        name: orderToPay.current.customer?.name ?? result.cardData.holderName,
        associationId: result.cardData.associationId,
        tag: result.cardData.number,
      };

      const isPosPaid = kiosk?.configSymstem.some(
        (it) => it.systemType === SystemType.pospayOrderPad
      );
      const orderWithCustomer: IOrder = {
        ...orderToPay.current,
        customer,
      };
      orderToPay.current = null;

      const syncResult = await DoCashlessCosumeUseCase(
        cashlessRepository,
        orderWithCustomer,
        isPosPaid ? TransactionType.pospaid : TransactionType.cashless,
        kiosk
      );

      if (syncResult.success) {
        setIsProcessing(false);
        paymentRef.current.resolver({
          order: orderWithCustomer,
          pedidoPos: syncResult.pedidoPOS,
          paymentType: isPosPaid
            ? TransactionType.pospaid
            : TransactionType.cashless,
          payment: {
            paymentMethod: TransactionType.cashless,
          } as Payment,
        });
      } else {
        setIsProcessing(false);
        paymentRef.current.rejecter(
          syncResult?.message ?? "Falha ao sincronizar consumo"
        );
      }
    } catch (error) {
      setIsProcessing(false);
      handleActiveIdleTimer();
      GetError(
        error,
        "callCashless",
        "PaymentProvider",
        undefined,
        ErrorCodeType.PAYMENT
      );
      paymentRef.current.rejecter(error);
    }
  }, [handleActiveIdleTimer, kiosk, showLoading]);

  const callWallet = useCallback(async () => {
    if (!orderToPay.current) {
      setIsProcessing(false);
      handleActiveIdleTimer();
      paymentRef.current.rejecter({
        code: ErrorCodeType.PAYMENT,
        message: "Pedido não encontrado!",
        caller: "callWallet",
        file: "PaymentProvider",
      } as IError);
      return;
    }
    try {
      setWalletRef(true);
      const result = await walletRef.current?.open(orderToPay.current);
      setWalletRef(false);
      if (result) {
        orderToPay.current = result.order;
        const customer: ICustomer = {
          ...orderToPay.current.customer,
          document:
            orderToPay.current.customer?.document ??
            result.walletTransaction.log.meepUser.cpf,
          name:
            orderToPay.current.customer?.name ??
            result.walletTransaction.log.meepUser.name,
          tag: result.walletTransaction.log.tag,
        };

        const order = orderToPay.current;
        orderToPay.current = null;
        await addOrderLog(
          orderToPay.current,
          `Adicionado desconto de etiqueta tag: ${order.customer?.tag}`
        );
        const payment = {
          amount: result.order.totalValue,
          success: true,
          paymentMethod: TransactionType.wallet,
          FinancialTransactionModel: {
            TipoAdquirente: 10, // meep
          },
          walletReceipt: result.walletTransaction.log,
        } as Payment;
        const _order = {
          ...order,
          customer,
          paymentWallet: result.walletTransaction,
          payment,
        };
        setIsProcessing(false);
        paymentRef.current.resolver({
          order: _order,
          paymentType: TransactionType.wallet,
          payment,
        });
      }
    } catch (error) {
      setWalletRef(false);
      setIsProcessing(false);
      handleActiveIdleTimer();
      GetError(
        error,
        "callWallet",
        "PaymentProvider",
        undefined,
        ErrorCodeType.PAYMENT
      );

      paymentRef.current.rejecter(error);
    }
  }, [addOrderLog, handleActiveIdleTimer]);

  const onSelectType = useCallback(
    async (type: TransactionType, nominalCardData?: ICardCashless) => {
      if (isProcessing) {
        return;
      }
      
      setIsProcessing(true);
      clearPinpadValues();
      setShowTypeSelection(false);
      await addOrderLog(
        orderToPay.current,
        `Tipo de pagamento: ${TransactionType[type]}`
      );
      switch (type) {
        case TransactionType.credit:
          setInProgress(true);
          callPinpad(type);
          break;
        case TransactionType.debit:
          setInProgress(true);
          callPinpad(type);
          break;
        case TransactionType.voucher:
          setInProgress(true);
          callPinpad(type);
          break;
        case TransactionType.cashless:
          if (nominalCardData) {
            callNominalCashless(nominalCardData);
          } else {
            callCashless();
          }
          break;
        case TransactionType.pospaid:
          callCashless();
          break;
        case TransactionType.pix:
          callPix();
          break;
        case TransactionType.wallet:
          callWallet();
          break;
        default:
          setIsProcessing(false);
          break;
      }
    },
    [
      isProcessing,
      addOrderLog,
      callCashless,
      callPinpad,
      callPix,
      callNominalCashless,
      clearPinpadValues,
      callWallet,
    ]
  );

  window.showMessageFromClient = (
    message: string,
    comando: number,
    severit: string
  ) => {
    setMessage(
      message
        .replace("SOLICITE A SENHA", "Digite sua senha")
        .replace("Transacao Aprov.", "Aguarde, em processamento...")
    );
  };

  window.showKeyBoard = (min: number, max: number, message: string) => {
    setKeyboardValues({ min, max, message });
  };

  window.getKeyboardValue = () => {
    const keyValue = window.keyboardValue;
    return keyValue;
  };

  const closeSelectionModal = useCallback(() => {
    setShowTypeSelection(false);
    setIsProcessing(false);
    handleActiveIdleTimer();
    orderToPay.current = null;
    paymentRef.current.rejecter({
      code: ErrorCodeType.PAYMENT,
      message: "Fechado pelo usuário",
      caller: "closeSelectionModal",
      file: "PaymentProvider",
    } as IError);
  }, [handleActiveIdleTimer]);

  const open = useCallback(
    (
      order: IOrder,
      paymentType?: TransactionType,
      nominalCardData?: ICardCashless
    ) => {
      handleDisableIdleTimer();
      setIsProcessing(false);
      orderToPay.current = order;

      if (order.totalValue === 0) {
        const paymentResult = {
          order,
          paymentType: TransactionType.money,
          payment: {
            success: true,
            amount: 0,
            paymentMethod: TransactionType.money,
            FinancialTransactionModel: {},
          },
        } as IPaymentResult;

        return Promise.resolve(paymentResult);
      }

      paymentRef.current = createDefferedPromise<IPaymentResult>();

      if (!paymentType) {
        setShowTypeSelection(true);
      } else {
        onSelectType(paymentType, nominalCardData);
      }
      return paymentRef.current.promise;
    },
    [handleDisableIdleTimer, onSelectType]
  );

  useImperativeHandle(ref, () => ({ open }));

  if (!kiosk) {
    return null;
  }

  return (
    <div>
      <BottomModal
        showModal={showTypeSelection}
        onClickClose={closeSelectionModal}
      >
        <SelectCardType //TODO: Refazer este cara
          kiosk={kiosk}
          total={orderToPay.current?.totalValue ?? 0}
          onSelect={onSelectType}
          rechargePayment={rechargePayment}
          isProcessing={isProcessing}
        ></SelectCardType>
      </BottomModal>

      <Modal open={!!keyboardValues}>
        <Fragment>
          <KeyboardPimpad
            total={orderToPay.current?.totalValue ?? 0}
            enableInstallment={kiosk.enableInstallment}
            keyboardValues={keyboardValues}
            onSendInstallment={(parcela) => {
              installment.current = parcela;
            }}
            onSend={() => {
              setKeyboardValues(null);
              setMessage("");
            }}
            onCancel={() => {
              setKeyboardValues(null);
              setMessage("");
            }}
          />
        </Fragment>
      </Modal>

      <Dialog open={inProgress || message !== ""} fullScreen>
        <div
          style={{
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "Ubuntu",
            fontSize: 32,
            padding: 32,
          }}
        >
          {message.length > 0 ? message : "Aguarde, em processamento..."}
          <Pinpad color={"var(--primary)"} style={{ marginTop: 50 }} />
        </div>
      </Dialog>
      <Pix ref={pixRef} />
      <UseCashless ref={cashlessRef} />
      {walletRefState && <UseWallet ref={walletRef} />}
      <UseNominalCashless ref={nominalCashlessRef} />
    </div>
  );
};

export default forwardRef(PaymentProvider);
